import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Injector, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, RouterLink, RouterModule } from '@angular/router';
import { PaymentModalComponent } from '@components/payment-modal/payment-modal.component';
import { SleekPlanLoaderComponent } from '@modules/sleekplan/sleek-plan-loader.component';
import { NgbCollapseModule, NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MainMenuStateService } from '@services/main-menu-state.service';
import { NavLayoutService } from '@services/nav-layout.service';
import { AbilityService, Action, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, DropdownButtonComponent, SwitcherComponent, TagComponent } from 'mbs-ui-kit';
import { ModalService } from 'mbs-ui-kit/modal/modal.service';
import { ToastService } from 'mbs-ui-kit/toast';
import { MbsPopupType, MbsSize } from 'mbs-ui-kit/utils';
import { Balance } from 'mbs-ui-shared/src/lib/components/components/models/license.model';
import { SupportPortalFacade } from 'mbs-ui-shared/src/lib/facades/support-portal.facade';
import { POST_PAYMENT_TOKEN } from 'mbs-ui-shared/src/lib/tokens/post-payment.token';
import { AppHeaderBaseComponent } from 'mbs-ui-shared@components/app-header/app-header-base-component';
import { DotSubType, DotType, MenuItemType } from 'mbs-ui-shared@components/app-header/app-header.types';
import { LicenseModeLabelComponent } from 'mbs-ui-shared@components/licenses/components/license-mode-label/license-mode-label.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AdministratorsFacade } from 'mbs-ui-shared@facades/administrators.facade';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { Administrator, LicenseType, PermissionsEnum, UiStorageKey } from 'mbs-ui-shared@models';
import { AgentType, ComputerModeAdapter, ComputersMode } from 'mbs-ui-shared@models/Computer';
import { LicenseMode } from 'mbs-ui-shared@models/ProviderData';
import AccountManager from 'mbs-ui-shared@modules/support-portal/AccountManager';
import { AppEnvStateService } from 'mbs-ui-shared@services/app-env-state.service';
import { AppPersistentStateService } from 'mbs-ui-shared@services/app-persistent-state.service';
import { ErrorHandlerService } from 'mbs-ui-shared@services/error-handler.service';
import { PaymentMethodsService } from 'mbs-ui-shared@services/payment-methods.service';
import { PremiumSupportService } from 'mbs-ui-shared@services/premium-support.service';
import { combineLatest, noop, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { appHeaderElementSelectors } from './app-header.constants';
import { MainMenu } from './app-header.menu';

@UntilDestroy()
@Component({
  selector: 'mbs-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  host: { '(window:focus)': 'onFocusHandler()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    DropdownButtonComponent,
    NgbCollapseModule,
    NgbDropdownModule,
    NgbTooltipModule,
    SleekPlanLoaderComponent,
    RouterLink,
    CanPipe,
    I18NextPipe,
    TagComponent,
    SwitcherComponent,
    LicenseModeLabelComponent
  ]
})
export class AppHeaderComponent extends AppHeaderBaseComponent {
  public isPostPayment = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);
  public errorHandlerService = inject(ErrorHandlerService);
  private menu = inject(MainMenu);
  private auth = inject(AuthFacade);
  private loginFacade = inject(LoginFacade);
  private ability = inject(AbilityService);
  private modalService = inject(ModalService);
  private stateService = inject(AppEnvStateService);
  private computersFacade = inject(ComputersFacade);
  private adminsFacade = inject(AdministratorsFacade);
  private menuState = inject(MainMenuStateService);
  private i18nPipe = inject(I18NextPipe);
  private appPersistentState = inject(AppPersistentStateService);
  private applicationStateFacade = inject(ApplicationStateFacade);
  private licensesFacade = inject(LicensesFacade);
  private premiumSupportService = inject(PremiumSupportService);
  private paymentMethods = inject(PaymentMethodsService);
  private supportPortalFacade = inject(SupportPortalFacade);
  private toastService = inject(ToastService);
  private injector = inject(Injector);
  protected router = inject(Router);
  protected navLayout = inject(NavLayoutService);

  public readonly MbsSize = MbsSize;
  public readonly MbsPopupType = MbsPopupType;
  public readonly Permissions = PermissionsEnum;
  public readonly DotType = DotType;
  public readonly DotSubType = DotSubType;
  public readonly elementSelectors = appHeaderElementSelectors;

  protected isToastDisabled = signal(false);
  protected isNavbarCollapsed = true;
  protected appEnvState = this.stateService.appEnvState;

  public helpMenuItems: Array<MenuItemType> = [];
  public addMenuItems: Array<MenuItemType> = [];
  public accountManager = signal<AccountManager>(null);

  protected licenseModeNone = signal(false);
  private subscriptionEditLink = signal<string>(null);
  private canManageSubscription = signal(false);
  protected allowManagePaymentMethods = computed(
    () =>
      (!this.licenseModeNone() && this.canLicenseBuy() && (!this.balance()?.subscriptionEnabled || this.subscriptionEditLink())) ||
      (this.canManageSubscription() && this.subscriptionEditLink())
  );
  public unreadSupportMessagesCount = signal(0);

  protected menuItems = this.menuState.mainMenu;

  private showDot = signal<Partial<Record<DotType, boolean>>>({});
  protected dot = computed<Partial<Record<DotType, boolean>>>(() => ({
    ...this.menuState.dot(),
    ...this.showDot(),
    [DotType.Help]: !!this.showDot()[DotType.GetDemo]
  }));

  private needForceUpdateSupportPortalMessages = false;

  public currentUser = signal<Administrator & { displayName: string }>(null);
  protected isPremiumSupportEnabled = this.premiumSupportService.hasPremiumSupportLicense;
  protected showBuyPremiumButton = computed(() => this.ability.can('read', PermissionsEnum.LicenseBuy) && !this.isPremiumSupportEnabled());
  protected balance = signal<Balance & { balanceValue: number }>(null);
  protected showTryRMMBtn = signal(false);
  protected showAddBtn = signal(false);
  protected canLicenseBuy = signal(false);

  protected getNavbarButtonsClass = computed((): string =>
    this.navLayout.isVertical()
      ? ''
      : `navbar-buttons-${(this.showAddBtn() ? 10 : 0) + (this.showTryRMMBtn() ? 10 : 0) + (this.showBalanceBtn() ? 15 : 0)}`
  );
  protected showBalanceBtn = computed(
    () => this.ability.can('read', 'LicenseBuy') && this.currentUser()?.ProviderInfo.IsPostPayment && !!this.balance()
  );

  constructor() {
    super();

    this.checkToastsAbility(window.localStorage.getItem(UiStorageKey.DisableAllToasts));
    this.initStreams();
  }

  protected initStreams(): void {
    super.initStreams();

    this.applicationStateFacade.loadUiStorage();

    this.licensesFacade.loadSubscriptionEditLink();
    this.setAddMenuItems();

    this.auth.currentUser$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((admin) => {
      this.currentUser.set({ ...admin, displayName: this.displayName(admin) });
      this.licenseModeNone.set(admin?.ProviderInfo?.LicenseMode === LicenseMode.None);
      this.canManageSubscription.set(this.ability.can('read', PermissionsEnum.ManageSubscription));
      this.updateMenu();
    });

    this.licensesFacade.subscriptionEditLink.pipe(untilDestroyed(this)).subscribe((link) => this.subscriptionEditLink.set(link));

    this.router.events
      .pipe(
        filter((v) => v instanceof NavigationStart),
        filter((eventParams) => this.isUrlChanged(eventParams)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.isNavbarCollapsed = true;
        this.modalService.dismissAll();
        this.menuState.handleNavigation();
      });

    this.menuState.sidepanelOpened.pipe(untilDestroyed(this)).subscribe(() => (this.isNavbarCollapsed = true));

    const accountManager$ = this.supportPortalFacade.getAccountManager().pipe(shareReplay(1));

    accountManager$.pipe(untilDestroyed(this)).subscribe((accountManager) => {
      this.accountManager.set(accountManager);
      accountManager && this.updateHelpMenu(true);
    });

    combineLatest([accountManager$, this.applicationStateFacade.selectStorageWhenLoaded$])
      .pipe(untilDestroyed(this))
      .subscribe(([accountManager, storage]) => {
        this.showDot.update((dot) => ({
          ...dot,
          [DotType.MainUser]:
            this.ability.can('read', PermissionsEnum.HelpMarketing) &&
            storage[UiStorageKey.HideMainUserMenuBadge] !== 'true' &&
            !!accountManager,
          [DotType.GetDemo]: storage[UiStorageKey.HideGetDemoMenuBadge] !== 'true' && !!accountManager?.calendarUrl
        }));

        this.checkToastsAbility(storage[UiStorageKey.DisableAllToasts]);
      });

    timer(0, 60 * 1000)
      .pipe(
        map((value, index) => !index),
        untilDestroyed(this)
      )
      .subscribe((force) => this.updateSupportPortalMessages(force));

    this.licensesFacade.loadBalance();
    this.licensesFacade.balance
      .pipe(
        shareReplay(),
        filter(Boolean),
        catchError(() => of(null)),
        map((balance) => ({
          ...balance,
          balanceValue: Math.abs(balance?.balance) || 0
        }))
      )
      .subscribe((balance) => {
        this.balance.set(balance);
        this.canLicenseBuy.set(this.ability.can('read', 'LicenseBuy'));
      });
  }

  protected get homePageUrl(): string {
    return this.ability.can('read', 'Dashboard') ? RoutingPath.ApDashboard : RoutingPath.ApSettings;
  }

  private updateMenu(): void {
    const ability = (rule: string, action: Action = 'read') => this.ability.can(action, rule);
    const canRead = (rules: string | string[]) => (Array.isArray(rules) ? rules.every((r) => ability(r)) : ability(rules));

    this.helpMenuItems = this.menu.getHelpMenuItems(
      this.handleSupportAction.bind(this),
      this.accountManager(),
      this.currentUser(),
      this.showBuyPremiumButton()
    );

    // buttons
    this.showAddBtn.set(
      canRead(PermissionsEnum.RemoteManagement) ||
        canRead(PermissionsEnum.ManageCompanies) ||
        canRead(PermissionsEnum.GoogleApps) ||
        canRead(PermissionsEnum.LicenseBuy)
    );
    this.enableTryRMMWizardBtn();

    this.updateHelpMenu();
  }

  private setAddMenuItems() {
    const user = this.auth.currentUser$.pipe(filter(Boolean));
    const permissionFlags = this.licensesFacade.permissionFlags.pipe(filter(Boolean));

    combineLatest([user, permissionFlags])
      .pipe(untilDestroyed(this))
      .subscribe(([admin, permissionFlags]) => {
        this.addMenuItems = this.menu.getAddMenuItems(
          admin,
          permissionFlags,
          this.addComputerByMode.bind(this),
          this.openPaymentModal.bind(this)
        );
      });
  }

  private updateHelpMenu(recreate = false): void {
    recreate &&
      (this.helpMenuItems = this.menu.getHelpMenuItems(
        this.handleSupportAction.bind(this),
        this.accountManager(),
        this.currentUser(),
        this.showBuyPremiumButton()
      ));
    this.helpMenuItems.forEach((menu) => this.menuState.applyAppBaseHref(menu));
  }

  protected getActiveSubTitle(menuItem: MenuItemType): string {
    return this.getActiveChild(menuItem)?.name ?? '';
  }

  protected isActiveMenuClass(menuItem: MenuItemType): boolean {
    return !!this.getActiveSubTitle(menuItem);
  }

  protected getParentIcon(menuItem: MenuItemType): string {
    return this.getActiveChild(menuItem)?.iconClass ?? menuItem.iconClass;
  }

  private displayName(user: Administrator) {
    const firstName = user?.FirstName || '';
    const lastName = user?.LastName || '';

    return firstName || lastName ? `${firstName} ${lastName}`.trim() : null;
  }

  protected handleChangeToastVisibility(event: boolean): void {
    event ? this.toastService.enableToasts() : this.toastService.disableToasts();

    window.localStorage.setItem(UiStorageKey.DisableAllToasts, String(!event));
    this.applicationStateFacade.updateStorageSetting(UiStorageKey.DisableAllToasts, String(!event)).subscribe();
  }

  private checkToastsAbility(value: string): void {
    if ((this.errorHandlerService.isDebug || this.currentUser()?.ProviderInfo?.IsTest) && value === 'true') {
      this.isToastDisabled.set(true);
      this.toastService.disableToasts();
    }
  }

  protected handleLogout(): void {
    this.loginFacade.logout();
  }

  protected handleMyProfile() {
    void this.router.navigate([RoutingPath.ApSettings]);
  }

  public managePaymentMethods() {
    const link = this.subscriptionEditLink();

    this.paymentMethods.open(this.balance()?.subscriptionEnabled && link ? link : this.auth.subscriptionUrl);
  }

  private addComputerByMode(): void {
    const mode = ComputerModeAdapter.find((modeUrl) => this.router.url.includes(modeUrl.url))?.mode ?? ComputersMode.Computers;
    const url = this.getComputerUrlByMode(mode);
    const queryParamsHandling = this.isActiveRoute(url) ? 'merge' : '';

    void this.router.navigate([url], {
      queryParams: { addNew: mode === ComputersMode.RMM ? AgentType.RMM : AgentType.Backup },
      queryParamsHandling
    });
  }

  protected openPaymentModal(isDebt = false) {
    if (isDebt) {
      this.router.navigate([RoutingPath.ApPurchaseHistory, 'debt']);

      return;
    }

    this.modalService
      .openCustom(PaymentModalComponent, {
        data: {
          title: this.i18nPipe.transform('shared:paymentModal.title', { format: 'title' }),
          userId: this.currentUser()?.Id
        },
        size: MbsSize.sm
      })
      .finally(noop);
  }

  protected handleRMMGettingStartedWizard(): void {
    this.router.navigateByUrl(RoutingPath.ApGettingStartTryRMM);
  }

  private enableTryRMMWizardBtn(): void {
    if (
      this.currentUser()?.IsProvider &&
      this.licensesFacade.isTrialLicenseAvailable(LicenseType.RMM) &&
      !this.currentUser()?.IsSuperAdmin &&
      !this.ability.can('read', PermissionsEnum.RmmPerEndpoint)
    ) {
      this.computersFacade.applicationsCountAfterInit$
        .pipe(
          take(1),
          map((installedAppsByType) => (installedAppsByType.backup > 0 || installedAppsByType.ra > 0) && !installedAppsByType.rmm),
          switchMap((allowTryRMMBtn) => {
            if (!allowTryRMMBtn) {
              return of(false);
            }

            this.adminsFacade.loadData();

            return toObservable(this.adminsFacade.dataWhenLoaded, { injector: this.injector }).pipe(
              take(1),
              map((adminList) => {
                const provider = adminList.find((admin) => admin.isProvider);

                if (!provider) {
                  return true;
                }

                if (provider.license && provider.license.licenseType === LicenseType.RMM) {
                  return provider.license.isTrial;
                }

                return true;
              })
            );
          }),
          untilDestroyed(this)
        )
        .subscribe((showTryRMMBtn) => this.showTryRMMBtn.set(showTryRMMBtn));

      this.computersFacade.loadApplicationsCount();
    } else {
      this.showTryRMMBtn.set(false);
    }
  }

  protected hideDot(type: DotType): void {
    if (type === DotType.Notifications) {
      this.menuState.hideDot(type);

      return;
    }

    if (!this.showDot()[type]) return;

    if (type === DotType.MainUser) {
      this.applicationStateFacade.updateStorageSetting(UiStorageKey.HideMainUserMenuBadge, 'true').subscribe();
    }

    if (type === DotType.GetDemo) {
      this.applicationStateFacade.updateStorageSetting(UiStorageKey.HideGetDemoMenuBadge, 'true').subscribe();
    }
  }

  protected handleCopyAccountManagerEmail(): void {
    if (!this.accountManager()?.email) return;

    void navigator.clipboard.writeText(this.accountManager()?.email);
    this.toastService.success(this.i18nPipe.transform('shared:header.texts.emailCopied'));
  }

  protected getAccountManagerPhotoURL(): string {
    if (!this.accountManager()?.fullPhotoUrl) return '';

    const photoPathIndex = this.accountManager()?.fullPhotoUrl.lastIndexOf(this.supportPortalFacade.photoPath);

    if (!photoPathIndex) return '';

    return `${location.origin}/${this.accountManager().fullPhotoUrl.substring(photoPathIndex)}`;
  }

  private getComputerUrlByMode(mode: ComputersMode): string {
    return ComputerModeAdapter.find((modeUrl) => mode === modeUrl.mode)?.url ?? '';
  }

  private getUrlWithoutParams(url: string) {
    if (!url) return url;

    const urlTree = this.router.parseUrl(url);

    urlTree.queryParams = {};
    urlTree.fragment = null;

    return urlTree.toString();
  }

  private isUrlChanged(eventParams: any): boolean {
    const currentUrl = this.getUrlWithoutParams(this.router.url) ?? '';
    const nextUrl = this.getUrlWithoutParams(eventParams?.url) ?? '';

    return currentUrl !== nextUrl;
  }

  // support messages
  private handleSupportAction(): void {
    if (this.unreadSupportMessagesCount()) this.needForceUpdateSupportPortalMessages = true;
    this.auth.redirectToSupportPortal();
  }

  private updateSupportPortalMessages(force = false): void {
    const itsTimeToCheckForMessages = (Date.now() - (this.appPersistentState.data?.supportMessages?.date || 0)) / 1000 / 60 >= 15;

    if (force || !this.appPersistentState.data?.supportMessages || itsTimeToCheckForMessages) {
      this.supportPortalFacade
        .unreadMessagesCount()
        .pipe(take(1), untilDestroyed(this))
        .subscribe({
          next: (result) => {
            const count = result?.count || 0;

            this.unreadSupportMessagesCount.set(count);
            this.appPersistentState.data.supportMessages = { date: Date.now(), count };
          },
          error: () => (this.appPersistentState.data.supportMessages = { date: Date.now(), count: 0 })
        });
    }
  }

  protected onFocusHandler(): void {
    if (!this.needForceUpdateSupportPortalMessages) return;

    this.needForceUpdateSupportPortalMessages = false;
    this.updateSupportPortalMessages(true);
  }
}
